/* ============================================================================
 * THE DECK'S LEAD ENDPOINT — one home for it, and one home for the `Lead` shape.
 *
 * Two places in the deck now capture a lead: the entrance gate, and the appendix's
 * "request the model". They write into the same sheet, so the endpoint must exist once —
 * a URL copied into a second file is a defect waiting for the day one of them changes.
 *
 * `code` is what separates them in the sheet. It is a parameter rather than a constant
 * because a request is not an entrance: an investor who asked for the model after reading
 * the appendix is a different signal from one who typed their email to get in, and the
 * sheet should be able to tell which slide earned the reply.
 * ========================================================================== */

export const LEAD_API = 'https://api.raisy.team/api/v1/waitlist/join';

export type Lead = { name: string; email: string };

const KEY = 'pd_lead';

/** The lead the gate captured, or `null` when there is none (a PDF export, a deep link). */
export function readLead(): Lead | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Lead>;
    return parsed?.email ? { name: parsed.name ?? '', email: parsed.email } : null;
  } catch {
    return null;
  }
}

/**
 * Best-effort, like the gate's own POST: the deck is never blocked or broken by the logging
 * backend being slow or down. Resolves either way, so a caller can `await` it without a guard.
 */
export async function postLead(lead: Lead, code: string): Promise<boolean> {
  try {
    const res = await fetch(LEAD_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: lead.name, email: lead.email, code, tab: 'PitchDeck' }),
    });
    const data = (await res.json()) as { status?: boolean; message?: string };
    if (!data.status) console.warn('[pitchdeck] waitlist:', data.message);
    return Boolean(data.status);
  } catch (err) {
    console.warn('[pitchdeck] waitlist unavailable', err);
    return false;
  }
}
